import {
  type CollectionReference,
  collection,
  collectionData,
  collectionSnapshots,
  type DocumentData,
  type DocumentReference,
  doc,
  docSnapshots,
  type Firestore,
  type FirestoreDataConverter,
  limit as fsLimit,
  orderBy,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  query,
  runTransaction,
  type Timestamp,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import {
  type FieldValuePair,
  ModelMoreThanOneError,
  ModelNotFoundError,
  NewIds,
  type OrderByPair,
  PartialWriteFailureError,
  type StringIdModel,
} from '@cdw-ae/common-data';
import { chunk, toString as toStr } from 'lodash';
import { from, map, type Observable, of, switchMap, throwError } from 'rxjs';

/**
 * Utility base class for data repositories for collections stored in firestore
 *
 * This class should be kept in sync with BackendFirestoreDataRepository as much
 * as possible
 *
 * @export
 * @class FrontendFirestoreDataRepository
 * @template T
 */
export class FrontendFirestoreDataRepository<T extends StringIdModel> {
  protected modelConverter: FirestoreDataConverter<T> = {
    toFirestore: (data: T) => this.toFirestore(data),
    fromFirestore: (snap: QueryDocumentSnapshot<DocumentData>) =>
      this.fromFireStore(snap) as T,
  };

  /**
   * Used to transform the object pulled from firestore to the shape of the
   * model before it is coerced to the the final model interface. Override this
   * method to add additional transformations.
   *
   * @protected
   * @param {QueryDocumentSnapshot<DocumentData>} snap The snapshot of the
   * object
   * @return {DocumentData} The final shape of the object
   * @memberof FrontendFirestoreDataRepository
   */
  protected enrichModel = (data: DocumentData): DocumentData => data;

  /**
   * Used to transform the model into the shape of the data stored in firestore
   * before it is stored. Override this method and to add additional
   * transformations.
   *
   * @protected
   * @param {T} model The model to be transformed
   * @return {DocumentData} The final shape of the object
   * @memberof FrontendFirestoreDataRepository
   */
  protected simplifyModel = (model: T): DocumentData => model;

  /**
   * Utility method for converting a Timestamp field oth a JS Date
   *
   * @protected
   * @param {Record<string, unknown>} fieldContainer The object containing the
   * field
   * @param {string} fieldName The name of the field to be updated
   * @memberof FrontendFirestoreDataRepository
   */
  protected fieldToDate = (
    fieldContainer: Record<string, unknown>,
    fieldName: string
  ): Record<string, unknown> => {
    const timestamp = fieldContainer?.[fieldName] as Timestamp;
    if (!timestamp) {
      return fieldContainer;
    }

    return {
      ...fieldContainer,
      [fieldName]: timestamp.toDate(),
    };
  };

  private toFirestore = (model: T): DocumentData => {
    // biome-ignore lint/correctness/noUnusedVariables: id must be destructured out to exclude it from docData
    const { id, ...docData } = this.simplifyModel(model);

    return docData;
  };

  private fromFireStore = (
    snap: QueryDocumentSnapshot<DocumentData>
  ): DocumentData => {
    const data = snap.data();

    // biome-ignore lint/complexity/useLiteralKeys: TypeScript requires bracket notation for index signature
    data['id'] = snap.id;

    return this.enrichModel(data);
  };

  constructor(protected firestore: Firestore) {}

  /**
   * Gets a single document from a firestore collection based on its id
   *
   * @protected
   * @param {string} collectionPath The path to the collection
   * @param {string} id The id of the document in the collection
   * @return {Observable<T>} An observable for watching the document. The
   * observable will throw a ModelNotFoundError and complete if a document with
   * the specified id is not found.
   * @memberof FrontendFirestoreDataRepository
   */
  protected getByCollectionPathAndId(
    collectionPath: string,
    id: string
  ): Observable<T> {
    const modelDocRef = this.getDocRef(collectionPath, id);

    return docSnapshots(modelDocRef).pipe(
      map((docSS) => {
        if (!docSS.exists()) {
          throw new ModelNotFoundError(
            `Document with id, ${id}, not found in collection, ${collectionPath}`
          );
        }

        return docSS.data();
      })
    );
  }

  /**
   * Gets a list of documents from firestore based on the specified field value
   * pairs
   *
   * @protected
   * @param {string} collectionPath The path to the collection
   * @param {FieldValuePair[]} [equalityConditions] A list of fields and values to check
   * to limit the list of documents. (Logical AND)
   * @param {number} [limit] Return only this amount of objects
   * @return {Observable<T[]>} An observable for watching the documents.
   * @memberof FrontendFirestoreDataRepository
   */
  protected getAllByCollectionPath(
    collectionPath: string,
    equalityConditions?: FieldValuePair[],
    orderByCondition?: OrderByPair,
    limit?: number
  ): Observable<T[]> {
    const collectionRef = this.getCollectionRef(collectionPath);
    const constraints: QueryConstraint[] =
      equalityConditions?.map(([f, v]) => where(f, '==', v)) || [];

    if (limit) {
      constraints.push(fsLimit(limit));
    }
    if (orderByCondition) {
      constraints.push(orderBy(orderByCondition[0], orderByCondition[1]));
    }

    return collectionData(query(collectionRef, ...constraints));
  }

  /**
   * Gets a single document from a firestore collection based on the specified
   * field value.
   *
   * @protected
   * @param {string} collectionPath The path to the collection
   * @param {string} field The field on the document to check
   * @param {*} value The value of the field on the document
   * @return {Observable<T>} An observable for watching the document. The
   * observable will throw a ModelNotFoundError and complete if a document with
   * the specified id is not found or it will throw a
   * ModelMoreThanOneError if more than one document is found with the specified
   * field value.
   * @memberof FrontendFirestoreDataRepository
   */
  protected getModelByValue(
    collectionPath: string,
    field: string,
    value: unknown
  ): Observable<T> {
    const collectionRef = this.getCollectionRef(collectionPath);
    const collectionQ = query(collectionRef, where(field, '==', value));

    return collectionSnapshots(collectionQ).pipe(
      switchMap((docSSs) => {
        if (!docSSs.length) {
          return throwError(
            () =>
              new ModelNotFoundError(
                `Document with with condition, "${field} = ${toStr(
                  value
                )}", not found in collection, ${collectionPath}`
              )
          );
        }

        if (docSSs.length > 1) {
          return throwError(
            () =>
              new ModelMoreThanOneError(
                `More than one document with condition, "${field} = ${toStr(
                  value
                )}" found in collection, ${collectionPath}`
              )
          );
        }

        const modelDoc = docSSs[0];

        return of(modelDoc.data());
      })
    );
  }

  /**
   * Writes a document at the specified collection path
   *
   * @protected
   * @param {string} collectionPath The path to the collection
   * @param {T} model The model to write
   * @param {boolean} [upsert=false] Whether to create the document if it
   * doesn't exist
   * @return {Observable<T>} An observable that emits once the
   * document has been written
   * @throws {ModelNotFoundError} Document not found with specified id and not
   * upserting
   * @memberof FrontendFirestoreDataRepository
   */
  protected writeByCollectionPath(
    collectionPath: string,
    model: Partial<T>,
    upsert = false
  ): Observable<T> {
    const modelDocRef = this.getDocRef(
      collectionPath,
      model.id as string | NewIds.StringID
    );

    return from(
      (async () => {
        await runTransaction(this.firestore, async (transaction) => {
          const modelDoc = await transaction.get(modelDocRef);
          if (!(upsert || modelDoc.exists())) {
            await Promise.reject(
              new ModelNotFoundError(
                `Document with id, ${model.id}, not found in collection, ${collectionPath}`
              )
            );
          }
          transaction.set(modelDocRef, model, { merge: true });
        });
        return runTransaction(this.firestore, async (transaction) => {
          const updatedModel = (await transaction.get(modelDocRef)).data();
          if (!updatedModel) {
            throw new ModelNotFoundError(
              `Document with id, ${model.id}, not found in collection, ${collectionPath}`
            );
          }
          return updatedModel;
        });
      })()
    );
  }

  /**
   * Writes many documents at the specified collection path. If models not found
   * in the firestore, new documents will be created.
   *
   * @protected
   * @param {string} collectionPath The path to the collection
   * @param {T[]} models The models to write
   * @param {number} [batchSize=500] The firestore api only allows batches of a
   * certain size (500 currently). This param allows you to control the batch
   * size.
   * @return {Observable<T[]>} An observable that emits once all
   * documents have been written
   * @throws {PartialWriteFailureError} If any batches fail to complete
   * @memberof FrontendFirestoreDataRepository
   */
  protected writeManyByCollectionPath(
    collectionPath: string,
    models: T[],
    batchSize = 500
  ): Observable<T[]> {
    const firestoreDocs = models.map((model: T): [DocumentReference<T>, T] => [
      this.getDocRef(collectionPath, model.id),
      model,
    ]);
    const chunkedDocs = chunk(firestoreDocs, batchSize);
    const batches = chunkedDocs.map((chk) => {
      const batch = writeBatch(this.firestore);
      chk.forEach(([modelDocRef, model]): void => {
        batch.set(modelDocRef, model);
      });

      return batch;
    });
    return from(
      (async () => {
        await Promise.allSettled(batches.map((b) => b.commit())).then(
          (results) => {
            const rejectedCount = results.filter(
              (r) => r.status === 'rejected'
            ).length;
            if (rejectedCount) {
              throw new PartialWriteFailureError(
                `${rejectedCount} out of ${results.length} batches failed to commit while writing ${models.length} documents to Firestore.`
              );
            }
          }
        );

        return firestoreDocs.map(([modelDocRef, model]) => ({
          ...model,
          id: modelDocRef.id,
        }));
      })()
    );
  }

  private getDocRef(
    collectionPath: string,
    id: string | NewIds.StringID
  ): DocumentReference<T> {
    const docRef =
      id === NewIds.StringID
        ? doc(this.getCollectionRef(collectionPath))
        : doc(this.firestore, collectionPath, id).withConverter(
            this.modelConverter
          );

    return docRef.withConverter(this.modelConverter);
  }

  private getCollectionRef(collectionPath: string): CollectionReference<T> {
    return collection(this.firestore, collectionPath).withConverter(
      this.modelConverter
    );
  }
}
